// ─────────────────────────────────────────────────────────────────────────────
// stores/printserverStore.ts
// ─────────────────────────────────────────────────────────────────────────────

import { defineStore } from 'pinia';
import { ref, watch, effectScope, type EffectScope } from 'vue';
import {
  collection, addDoc,
  doc, getDoc, getDocs, serverTimestamp, query, where, orderBy,
  writeBatch, deleteField,
  type DocumentData, type Query, type QuerySnapshot, type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/firebase/config';
import { liveAbo } from '@/utils/liveAbo';
import { mitAnspruchsWiederholung } from '@/utils/anspruch';
// Die Rollenweiche liegt IM STORE — Bauform und Begruendung wie bei
// `companyStore.subscribe()` und `userStore.subscribe()` (REQ-SEC-029 (e)
// Praez. 1 (4)): ein zweiter Ort, an dem die Rolle entschieden wird, ist ein
// zweiter Ort, an dem sie vergessen werden kann.
import { useAuthStore } from './authStore';
import type { ZugewiesenerDrucker } from '@/utils/printserverKaskade';

/**
 * Die Obergrenze von Firestore fuer die Werteliste eines `in`-Filters
 * (T-M45.9, REQ-SEC-029 (e) Praez. 3 (1)).
 *
 * Der Anspruchsweg von {@link usePrintserverStore}`.subscribeAlleMandanten`
 * setzt die GANZE Kennungsliste des Anspruchs in EINEN `in`-Filter. Das traegt
 * nur, weil ein Benutzer hoechstens `MAX_MANDANTEN_JE_BENUTZER` Mandanten
 * erhaelt (`utils/einladung.ts`, heute 30). **Dass diese Grenze nicht ueber
 * dieser liegt, haelt eine Zusicherung in `test/firestoreIndexes.test.ts`
 * (Abschnitt 2c)** — sie faellt, sobald jemand die Mandantengrenze anhebt,
 * ohne den Anspruchsweg zu stueckeln.
 */
export const IN_FILTER_GRENZE = 30;

export interface PrintServer {
  id:        string;           // Firestore document ID
  // Optional (REQ-DM-064 Praez. 5 (d), T-M47.3 Kriterium 7): die `uuid` entsteht
  // NUR bei der Neuanlage; ein Bestandsdokument muss sie nicht tragen. Ein
  // Pflicht-`string` haette dazu verleitet, beim Laden oder Bearbeiten einen
  // Wert zu erfinden (Befund B1, OP-261). Kein technischer Leser.
  uuid?:     string;           // Beschreibende UUID des Raspberry Pi (optional)
  name:      string;
  companyId: string;           // Verbundene Firma
  locationId?: string;         // Zugeordneter Standort (fuer Standort-Gruppierung / -Offline-Ableitung)
  // ── Laufzeitfelder (JEDES mit benanntem Schreiber, REQ-DM-064) ──────────
  //
  // Die Menge dessen, was der Daemon in dieses Dokument schreibt, ist
  // ABSCHLIESSEND und liegt in `print-server/src/heartbeat.js`
  // (`PRINTSERVER_DAEMON_FIELDS`, aus den DREI Nutzlasten abgeleitet — bis zum
  // 2026-09-18 waren es zwei und sieben Felder, seit T-M41.9 sind es drei und
  // neun, REQ-DM-064 Praez. 3 (a)/(b); seit T-M42.4 zehn Felder
  // (`plannedShutdownAt`, Praez. 4), seit T-M53.2 elf (`anydeskId`, Praez. 7)
  // — die Zahl „neun" galt bis zum 2026-09-25). Der
  // Waechter `dashboard/test/printserverLaufzeitfelder.test.ts` haelt die hier
  // gefuehrten und in den Ansichten GELESENEN Laufzeitfelder dagegen — ein
  // Leser ohne Schreiber laesst ihn rot umfallen.
  //
  // Zwei Takte, und der Unterschied ist der Grund fuer die Reihenfolge unten:
  //   • 30-Sekunden-Takt (`buildHeartbeatPayload`, REQ-RS-030): `status`,
  //     `lastPing`, `appVersion` (+ `updatedAt`, hier nicht gelesen).
  //   • Statuswechsel (`buildStatusPayload`): zusaetzlich `nodeVersion`,
  //     `ipAddress` und — nur beim Uebergang auf `online` — `connectedSince`.
  //     Sie aendern sich zur Laufzeit nicht und gehoeren deshalb NICHT in den
  //     Takt. An einem Bestandsgeraet vor dem naechsten Statuswechsel fehlen
  //     sie am Dokument; die Ansicht zeigt dann den Strich mit dem Hinweis
  //     „noch nicht gemeldet" (REQ-OPS-034 Praez. 3).
  //   • NACHGEFUEHRT AM 2026-10-27 (T-M53.2/T-M53.3, REQ-DM-064 Praez. 7): beim
  //     Uebergang auf `online` zusaetzlich `anydeskId` — aber NUR bei gueltigem
  //     Wert (6 bis 12 Ziffern, unveraendert). Fehlt es, heisst das „unbekannt"
  //     und ist anders als bei den drei Selbstmeldungen darueber auch DAUERHAFT
  //     moeglich (kein AnyDesk, ID nicht lesbar); die Detailansicht nennt dafuer
  //     einen eigenen Hinweis. Ein frueher gemeldeter Wert bleibt dann stehen.
  //     Bis zum 2026-10-27 kannte dieser Typ das Feld nicht.
  // `status` schreibt ausserdem `checkprintserverheartbeats` (REQ-RS-030).
  status?:              'online' | 'offline';
  ipAddress?:           string;          // Selbstmeldung des Pi (NICHT printers.ipAddress, REQ-DM-062)
  anydeskId?:           string;          // Selbstmeldung des Pi beim Start (REQ-DM-064 Praez. 7)
  nodeVersion?:         string;          // Node.js-Fassung auf dem Pi (Flottenwartung)
  appVersion?:          string;          // Versionsnummer der Pi-App (REQ-OPS-031)
  connectedSince?:      unknown;         // Timestamp: Beginn der laufenden Verbindung
  lastPing?:            unknown;         // Timestamp: letztes Lebenszeichen (Heartbeat)
  // Alt-Feld ohne heutigen Schreiber: es steht nur noch in Dokumenten aus der
  // Zeit vor `lastPing` und wird ausschliesslich als Rueckfall daneben gelesen.
  lastSeen?:            unknown;         // Timestamp: letzter Heartbeat (Alt-Feld)
  // ⚠ `printerCount` ist am 2026-08-14 mit T-M17.10 ENTFALLEN (REQ-DM-064): das
  // Feld hat nie jemand geschrieben, und die Zahl der Drucker eines
  // Druckservers wird im Dashboard aus den ohnehin abonnierten `printers`
  // ABGELEITET (`utils/printserverPrinterCount.ts`). Ein Zaehlfeld waere eine
  // zweite Quelle fuer dieselbe Tatsache. NICHT wieder aufnehmen.
  // NACHGEFUEHRT AM 2026-09-18 (T-M41.9 / OP-154, REQ-DM-064 Praez. 3 (c)–(e)):
  // `updateAvailable` hat seit heute einen SCHREIBER — der Launcher ermittelt
  // die vom Kanal gefuehrte Version, der Daemon schreibt sie beim Statuswechsel
  // (`buildStatusPayload`). Bis dahin stand hier „(oder null)" und der Typ
  // liess `null` zu; das war eine Zusage ohne Gegenseite. Der Feldvertrag sagt
  // ausdruecklich: **eine Aussage oder GAR NICHTS** — bei Gleichstand LOESCHT
  // der Daemon das Feld, bei unerreichbarem Kanal laesst er es unveraendert
  // stehen. Nie `null`, nie ein Platzhalter. Berichtigt statt geloescht
  // (REQ-OPS-036 Praez. 6).
  updateAvailable?:     string;          // Version, die der Verteilkanal fuehrt (nur bei Abweichung)
  // ── Steuerung (Dashboard → Pi) ──────────────────────────────────────────
  autoRestartMinutes?:  number;          // 0 = deaktiviert; wirkt ab dem naechsten Start des Launchers
  restartRequestedAt?:  unknown;         // Timestamp: Dashboard fordert Neustart an
  // NEU AM 2026-09-18 (T-M41.9 / OP-148, REQ-RP-048 Festlegung 6): die QUITTUNG.
  // Das GERAET schreibt sie vor dem Beenden — sie ist damit ein Laufzeitfeld und
  // steht in `PRINTSERVER_DAEMON_FIELDS`. Ein Feld „Ergebnis" gibt es
  // ausdruecklich NICHT: der Erfolg ist ein `connectedSince`, das juenger ist
  // als `restartExecutedAt`. Weder dieses Feld noch `restartRequestedAt` wird je
  // geloescht; die Quittung gehoert deshalb nur dann zur Anforderung, wenn sie
  // NICHT AELTER ist (`utils/neustartQuittung.ts`).
  restartExecutedAt?:   unknown;         // Timestamp: das Geraet hat den Neustart ausgefuehrt
  createdAt?:           unknown;
}

export const usePrintserverStore = defineStore('printserver', () => {
  const servers   = ref<PrintServer[]>([]);
  const isLoading = ref(false);
  const error     = ref<string | null>(null);
  /**
   * Ob mindestens EINE Momentaufnahme angekommen ist (T-M17.9 / REQ-PS-027
   * Praez. 2 (f-neu), REQ-OPS-034 Praez. 3).
   *
   * `isLoading` taugt dafuer NICHT: es steht vor dem ersten `subscribe()`
   * ebenfalls auf `false`, und die beiden Druckeransichten abonnieren erst in
   * `onMounted`. Wer den leeren Bestand in diesem Fenster als Auskunft liest,
   * behauptet „kein Druckserver zugeordnet" fuer jede Zeile — eine falsche
   * Aussage ueber die KONFIGURATION, aus einem blossen Ladezustand gemacht.
   *
   * Bleibt bei einem Fehler bewusst `false`: dann liegt keine Auskunft vor, und
   * es soll auch keine behauptet werden. `unsubscribe()` setzt es nicht
   * zurueck — die zuletzt gelesene Liste bleibt im Speicher und bleibt gueltig.
   */
  const hasLoaded = ref(false);
  /**
   * Der LEBENDE Zustand des Abos (T-M18.6, Bauform aus `printerStore`).
   *
   * Eine Ansicht, die die Druckserverliste BRAUCHT — die Auftragsliste braucht
   * sie fuer ihren Druckserver-Filter —, leitet daraus ab, ob sie selbst
   * abonnieren muss, statt sich darauf zu verlassen, welche Seite vorher offen
   * war (`FleetView.vue` raeumt das Abo beim Verlassen ab). Gelesen wird das
   * ueber die reine Regel `utils/printserverSubscription.ts`.
   */
  const isSubscribed        = ref(false);
  const subscribedCompanyId = ref<string | null>(null);
  /**
   * DIE KENNUNGSLISTE DES ANSPRUCHSWEGES (T-M45.9, REQ-SEC-029 (e) Praez. 3).
   *
   * Gesetzt genau dann, wenn das laufende Abo die Druckserver MEHRERER
   * Mandanten des Anspruchs haelt (`where companyId in …`); sonst `null`.
   * `subscribedCompanyId` steht in diesem Fall ebenfalls auf `null` — das Abo
   * gehoert keinem EINZELNEN Mandanten, und die Ansichten, die ein Abo ihres
   * gewaehlten Mandanten brauchen (Auftragsliste, Detailansichten), setzen
   * deshalb ihr eigenes auf. **Ohne diese Liste waere der Anspruchsweg von der
   * mandantenuebergreifenden Sicht eines Admins nicht zu unterscheiden** — und
   * der Untertitel der Einstellungsansicht behauptete einem Kundenbenutzer
   * „aller Firmen" (Praez. 3 (3)).
   */
  const subscribedCompanyIds = ref<string[] | null>(null);
  let _unsub: Unsubscribe | null = null;

  /**
   * Die Form eines Abos: der Mandant (oder `null`), bei einem Abo ueber die
   * Liste des Anspruchs deren Kennungen, und das Ziel der Abfrage.
   */
  type AboAuftrag = {
    companyId:   string | null;
    companyIds?: readonly string[];
    ziel:        () => Query<DocumentData>;
  };

  /**
   * DAS ZULETZT GESTARTETE ABO — der Rueckweg von Hand (T-M35.2 Nacharbeit N1,
   * REQ-OPS-054 (a), REQ-OPS-053).
   *
   * ⚠ **WARUM DAS NICHT AUS `subscribedCompanyId` ABZULESEN IST:** genau in der
   * Lage, in der der Rueckweg gebraucht wird, steht dort schon `null` — der
   * Fehlerweg (`onFehler`) und der stille Fall (`onStill`) setzen es zurueck,
   * und der mandantenuebergreifende Weg fuehrt ohnehin `null`. Aus `null` liesse
   * sich weder „mandantenuebergreifend" noch „Mandant vergessen"
   * unterscheiden; wer es dennoch versuchte, gaebe einem Admin nach dem Klick
   * auf „Erneut verbinden" die Liste EINES Mandanten zurueck (die Liste
   * schruempfte still) oder gar keine.
   *
   * **Gemerkt wird deshalb der AUFTRAG, nicht sein Ergebnis.** Wiederaufgenommen
   * wird das Abo, das lief — mit demselben Ziel und derselben Reichweite; die
   * Rollenfrage wird dabei NICHT ein zweites Mal gestellt (sie ist beim ersten
   * Start beantwortet worden, und ein Rollenwechsel ohne neue Anmeldung gibt es
   * nicht — eine Anmeldung setzt den Store neu auf).
   *
   * **`unsubscribe()` LOESCHT DAS GEMERKTE ABSICHTLICH NICHT** — entschieden am
   * Verhalten des Hinweises und nicht aus Bequemlichkeit: der
   * Verbindungshinweis zeigt einen Bereich nur dann als tot, wenn eine
   * Fehlermeldung steht UND nicht abonniert ist (`error && !isSubscribed`).
   * Nach einem bewussten `unsubscribe()` steht keine Fehlermeldung; die Lage
   * „Rueckweg nach dem Verlassen einer Ansicht" ist also gar nicht erreichbar.
   * Das Gemerkte zu loeschen brächte nichts und naehme dem Rueckweg den Fall,
   * in dem eine Ansicht beim Aufraeumen abmeldet, waehrend der Fehler noch
   * steht.
   */
  let letztesAbo: AboAuftrag | null = null;

  /**
   * DIE EINE UEBERNAHMESTELLE DER MOMENTAUFNAHME (T-M35.2, REQ-OPS-038).
   *
   * Alle Abo-Wege unterscheiden sich in GENAU EINEM Stueck: dem Ziel der
   * Abfrage. Seit T-M45.9 (REQ-SEC-029 (e) Praez. 3) sind es DREI Ziele — der
   * mandantengefilterte Weg (`==`, ueber {@link subscribe} und die Weiche in
   * {@link subscribeAlleMandanten} bei genau einer Kennung), die Liste des
   * Anspruchs (`in`, Nicht-Admin mit zwei oder mehr Kennungen) und die
   * filterlose Sicht (allein fuer einen Admin). *Bis zum 2026-09-28 stand hier
   * „Beide Abo-Wege — der mandantengefilterte und der admin-vorbehaltene";
   * berichtigt statt geloescht, REQ-OPS-036 Praez. 6.* Alles Uebrige liegt hier und nur
   * hier: das Abraeumen der vorigen Subscription (sonst leben nach einem
   * Mandantenwechsel zwei Listener, und der aeltere schreibt die fremde Liste
   * zurueck), die Reihenfolge, die Ladezustaende und die drei Rueckwege.
   *
   * *Warum nicht zwei Zweige mit je eigener Uebernahme: die Sortierung, die
   * Ruecknahme des „abonniert"-Merkmals im Fehlerfall (OP-151) und die
   * Unberuehrtheit von `hasLoaded` sind Entscheidungen, die an zwei Stellen
   * auseinanderlaufen wuerden — und die zweite faellt keinem Waechter auf.*
   *
   * Fehlerweg, Protokoll und Rueckzugsstaffel kommen aus der EINEN
   * Bildungsstelle (REQ-OPS-053 (e), T-M21.4); das `onSnapshot(` selbst steht
   * seit dem 2026-08-18 in `utils/liveAbo.ts`. **Es bleibt bei EINEM Aufruf von
   * `liveAbo` in dieser Datei** — ein zweiter waere die naechste Fassung des
   * Fehlerwegs.
   *
   * @param opts.companyId der Mandant des Abos oder `null` fuer die
   *   mandantenuebergreifende Sicht. `null` geht ausdruecklich NICHT als
   *   Mandant an `liveAbo`: die Protokollzeile soll nicht behaupten, das Abo
   *   laufe unter einem Mandanten.
   */
  function starteAbo(opts: AboAuftrag) {
    _unsub?.();
    _unsub = null;
    // Der Auftrag wird gemerkt, BEVOR irgendetwas schiefgehen kann — der
    // Rueckweg von Hand nimmt genau diesen wieder auf (siehe `letztesAbo`).
    letztesAbo = opts;
    isLoading.value           = true;
    error.value               = null;
    isSubscribed.value        = true;
    subscribedCompanyId.value = opts.companyId;
    subscribedCompanyIds.value = opts.companyIds ? [...opts.companyIds] : null;
    _unsub = liveAbo<QuerySnapshot>({
      collection: 'printservers',
      ...(opts.companyId === null ? {} : { companyId: opts.companyId }),
      ziel: opts.ziel,
      onData: (snap) => {
        // EINE Reihenfolge fuer alle Wege: der filterlose des Admins bekommt
        // sie schon von Firestore, der mandantengebundene und die Liste des
        // Anspruchs (T-M45.9) erst hier — sortiert wird in jedem Fall nach
        // `name`, und zwar nach derselben Regel.
        servers.value = snap.docs
          .map(d => ({ id: d.id, ...d.data() } as PrintServer))
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'de'));
        isLoading.value = false;
        hasLoaded.value = true;
      },
      // `hasLoaded` bleibt im Fehlerzweig bewusst unberuehrt (siehe oben):
      // es gibt keine Auskunft, also wird auch keine behauptet. Das
      // „abonniert"-Merkmal wird zurueckgenommen (OP-151).
      onFehler: (err) => {
        error.value               = err.message;
        isLoading.value           = false;
        isSubscribed.value        = false;
        subscribedCompanyId.value = null;
        subscribedCompanyIds.value = null;
      },
      // Der Rueckweg (REQ-OPS-053 (a), Gegenrichtung): kommt die
      // Wiederaufnahme durch, faellt auch der Fehlzustand.
      onErholt: () => {
        error.value               = null;
        isSubscribed.value        = true;
        subscribedCompanyId.value = opts.companyId;
        subscribedCompanyIds.value = opts.companyIds ? [...opts.companyIds] : null;
      },
      onStill: () => {
        isLoading.value           = false;
        isSubscribed.value        = false;
        subscribedCompanyId.value = null;
        subscribedCompanyIds.value = null;
      },
    });
  }

  /**
   * Abonniert die Druckserver — MIT Mandantenkennung mandantengebunden.
   *
   * ── WARUM DIE GRENZE IN DIE ABFRAGE GEHOERT (REQ-OPS-032 Praez. 1) ───────
   * Bis zum 2026-08-14 fragte dieser Store ausnahmslos nach dem Feld `name`
   * sortiert ueber ALLE Mandanten (OP-79) und raeumte die vorige Subscription nicht ab. Eine
   * Auswahl, die daraus clientseitig nachfiltert, erfuellt die Anforderung
   * ausdruecklich NICHT: die fremden Dokumente sind dann bereits im Speicher
   * des Browsers. Der mandantengebundene Zweig setzt `where` deshalb in die
   * ABFRAGE.
   *
   * ── WARUM DER MANDANTENZWEIG NICHT IN DER ABFRAGE SORTIERT ──────────────
   * Ein Gleichheitsfilter auf die Mandantenkennung PLUS eine Sortierung nach
   * dem Feld `name` IN DER ABFRAGE braeuchte den
   * zusammengesetzten Index `printservers: companyId ASC, name ASC`. Den gibt
   * es in `firebase/firestore.indexes.json` NICHT (dort steht fuer
   * `printservers` seit dem 2026-09-19 gar kein Eintrag mehr; bis dahin
   * allein der verwaiste `status ASC, lastPing ASC`, entfernt mit
   * T-M40.4), und Firestore
   * beantwortet eine Abfrage ohne ihren Index mit FAILED_PRECONDITION — die
   * Auswahl bliebe im Betrieb leer (REQ-DM-050a, REQ-OPS-039). Sortiert wird
   * deshalb NACH dem Eintreffen der Momentaufnahme im Code; der reine
   * Gleichheitsfilter kommt mit dem automatischen Einzelfeld-Index aus.
   * *(Wer den Index nachliefert, darf die Sortierung in die Abfrage
   * zurueckholen — dann aber erst nach der Pruefung „Aktiviert" im Zielprojekt,
   * REQ-OPS-039 (a)–(c).)*
   *
   * ── ⚠ DER FILTERLOSE ZWEIG IST AM 2026-09-01 ENTFALLEN (T-M30.7,
   *    Nacharbeit N1a; berichtigt statt geloescht, REQ-OPS-036 Praez. 6) ────
   * BIS DAHIN STAND HIER: „Der filterlose Zweig bleibt fuer die uebrigen
   * Verwender bestehen (`FleetView`, `PrintersView`, `PrinterDetailView`, die
   * Einstellungen) — OP-79 ist mit T-M18.6 fuer den Weg der Auftragsliste
   * geschlossen, nicht fuer alle Verwender." Der Zweig lautete
   * eine reine Sortierung nach dem Feld `name` ohne jeden Filter und lief, wenn
   * `subscribe()` ohne Kennung gerufen wurde. *(Der Wortlaut nennt die
   * Abfrageform hier absichtlich in Worten statt als Code: der Index-Waechter
   * `dashboard/test/firestoreIndexes.test.ts` erhebt Abfrageformen aus dem
   * Quelltext und sieht Kommentare NICHT als solche — ein zitierter Aufruf
   * waere fuer ihn eine Abfrage, die es nicht gibt.)*
   *
   * **ER IST FORT, UND DER PARAMETER IST PFLICHT.** Gegen die gebauten Rules
   * (REQ-SEC-029 (a)/(e)) wird eine Abfrage ohne Gleichheitsfilter auf die
   * Mandantenkennung fuer die Rolle `user` ALS GANZES abgewiesen — nicht
   * gekuerzt. Drei Ansichten (`PrintserverView`, `PrintserverSettingsView` und
   * die zwei Detailansichten) haetten damit fuer jeden Kundenbenutzer eine
   * leere Liste, eine Fehlermeldung und die fuenffache Rueckzugsstaffel
   * gezeigt. **OP-79 ist damit fuer ALLE Verwender geschlossen.**
   *
   * *Warum ersatzlos und nicht als stehengebliebene Attrappe: ein Aufrufer
   * ohne Kennung bricht ab jetzt in der TYPPRUEFUNG — das ist die stumpfere
   * Waffe als jeder Textwaechter und die einzige, die auch eine kuenftige
   * Aufrufstelle trifft, an die niemand gedacht hat. Wer einen Druckserver
   * AUSSERHALB des gewaehlten Mandanten braucht (der Admin an der Adresszeile),
   * nimmt {@link fetchServerById} — einen Einzelabruf, den die Rules erlauben.*
   *
   * ── ⚠ NACHTRAG 2026-09-10 (T-M35.2, REQ-SEC-029 (e) Praez. 1; berichtigt
   *    statt geloescht, REQ-OPS-036 Praez. 6) ───────────────────────────────
   * DER ABSATZ DARUEBER GILT WEITER, UND ZWAR FUER DIESE FUNKTION: `subscribe`
   * behaelt seinen Pflichtparameter, und ein filterloser Aufruf DIESES Weges
   * bleibt ein Typfehler. **Es gibt seit dem 2026-09-10 daneben einen ZWEITEN
   * Weg** — {@link subscribeAlleMandanten} —, der die mandantenuebergreifende
   * Sicht der Druckserver-Einstellungen bedient. **Er ist NICHT die Rueckkehr
   * des hier entfallenen Zweiges:** jener lief fuer JEDEN Aufrufer und jede
   * Rolle, der neue ist im Store selbst admin-vorbehalten und faellt fuer alle
   * uebrigen auf genau diese Funktion zurueck. Der Vorbehalt steht VOR der
   * Abfrage; siehe die Begruendung dort.
   * **NACHTRAG 2026-09-28 (T-M45.9, REQ-SEC-029 (e) Praez. 3; berichtigt statt
   * geloescht, REQ-OPS-036 Praez. 6):** admin-vorbehalten ist seither allein
   * die FILTERLOSE Form jenes Weges. Auf diese Funktion (genauer: ihren
   * Abfrageaufbau `abonniereMandant`) faellt ein Nicht-Admin nur noch bei
   * GENAU EINER Kennung im Anspruch zurueck; mit zwei oder mehr erhaelt er die
   * Liste des Anspruchs, mit keiner kein Abo.
   */
  function subscribe(companyId: string) {
    // Ein Aufrufer, der EINEN Mandanten waehlt, beendet die Folge des
    // Anspruchs (T-M45.9): sonst setzte eine spaetere Claim-Aenderung einer
    // mandantengebundenen Ansicht die Reichweite der Einstellungsansicht auf.
    beendeAnspruchsFolge();
    abonniereMandant(companyId);
  }

  /**
   * Der mandantengefilterte Abfrageaufbau — EINE Stelle fuer beide Aufrufer:
   * {@link subscribe} von aussen und die Weiche in
   * {@link subscribeAlleMandanten} (Ausgang „genau eine Kennung").
   * *Weitergereicht, nicht nachgebaut: ein zweiter Aufbau waere eine zweite
   * Stelle, an der der Filter fehlen kann.*
   */
  function abonniereMandant(companyId: string) {
    starteAbo({
      companyId,
      // Ohne Sortierung IN DER ABFRAGE — die Begruendung steht oben
      // (der zusammengesetzte Index fehlt bewusst).
      ziel: () => query(collection(db, 'printservers'), where('companyId', '==', companyId)),
    });
  }

  /**
   * Abonniert die Druckserver **ALLER Mandanten — und zwar nur fuer einen
   * Admin** (T-M35.2, REQ-SEC-029 (e) Praez. 1, REQ-OPS-032 Praez. 3).
   *
   * ── ⚠ WARUM DAS NICHT DER AM 2026-09-01 ENTFALLENE ZWEIG IST (2026-09-10) ─
   * Der entfallene Zweig war **jedem Aufrufer offen**: wer `subscribe()` ohne
   * Kennung rief, setzte eine Abfrage ohne Mandantenfilter ab — und die Rules
   * filtern nicht, sie WEISEN AB. Fuer die Rolle `user` war das Ergebnis nicht
   * eine kuerzere Liste, sondern KEINE: leere Ansicht, Fehlermeldung und die
   * fuenffache Rueckzugsstaffel (der Befund von T-M30.7, Nacharbeit N1a).
   * **Diese Funktion ist der Gegenentwurf, nicht die Rueckkehr:** der Anspruch
   * wird VOR der Abfrage geprueft, und fuer einen Nicht-Admin wird die
   * mandantenuebergreifende Abfrage GAR NICHT ERST ABGESETZT — er geht auf den
   * mandantengefilterten Weg zurueck (oder abonniert nicht, wenn kein Mandant
   * gewaehlt ist). Die Rules erlauben einem Admin `printservers` ohne
   * Mandantenbindung seit dem 2026-09-01 (REQ-SEC-029 (a)); **es waechst kein
   * Recht, ein bestehendes wird genutzt.** Die Sperre hier ist Bequemlichkeit
   * und nicht die Durchsetzung (Praez. 1 (4)).
   *
   * **DREI AUSGAENGE, und sie sind vollstaendig:**
   *   (1) Admin → Live-Abo OHNE Mandantenfilter, sortiert nach dem Feld `name`
   *       in der Abfrage selbst. `subscribedCompanyId` bleibt `null` — das
   *       Abo gehoert keinem Mandanten, und ein eingetragener Mandant waere
   *       hier eine Falschaussage.
   *   (2) kein Admin, aber eine Kennung → {@link subscribe} mit dieser Kennung.
   *       **Weitergereicht, nicht nachgebaut:** ein zweiter Abfrageaufbau waere
   *       eine zweite Stelle, an der der Filter fehlen kann.
   *   (3) kein Admin und keine Kennung → **kein Abo**. Dieselbe Lage wie
   *       bisher, wenn eine Ansicht ohne gewaehlten Mandanten geoeffnet wird:
   *       sie zeigt ihren leeren Zustand.
   *
   * ── WARUM DIE SORTIERUNG HIER IN DER ABFRAGE STEHEN DARF ─────────────────
   * Ohne Gleichheitsfilter ist es eine REINE Sortierung; dafuer genuegt der
   * automatische Einzelfeld-Index, es braucht KEINEN zusammengesetzten
   * (REQ-DM-050a, REQ-OPS-039) — `firebase/firestore.indexes.json` bleibt
   * unveraendert. Genau das haelt der Waechter
   * `dashboard/test/firestoreIndexes.test.ts` fest.
   *
   * ⚠ **BENANNTE GRENZE DER SORTIERUNG IN DER ABFRAGE (REQ-OPS-036):** eine
   * Sortierung nach einem Feld holt nur Dokumente, die dieses Feld FUEHREN.
   * Ein Druckserver-Dokument ohne Namen erschiene dem Admin hier also nicht,
   * waehrend der mandantengefilterte Weg (der ohne Sortierung in der Abfrage
   * auskommt) ihn zeigte. Der Name ist ein Pflichtfeld des Formulars und am
   * Typ nicht optional; die Lage betraefe allein ein von Hand oder ueber ein
   * Skript angelegtes Dokument ohne Namen.
   *
   * ⚠ **KEIN `limit`, KEIN Blaettern** — REQ-OPS-032 Praez. 3 (c) hat das
   * ausdruecklich vertagt (OP-202): ein `limit` ohne Blaettern machte Geraete
   * UNERREICHBAR. **Benannte Kostenfolge, gemessen statt verschwiegen:** ein
   * Live-Abo ueber die ganze Flotte empfaengt jeden Herzschlag jedes Geraets
   * (30-Sekunden-Takt, REQ-RS-030), solange die Seite offen ist. Betroffen ist
   * ausschliesslich ein Admin mit geoeffneter Einstellungsseite.
   *
   * ── ⚠ NACHTRAG 2026-09-28 (T-M45.9, REQ-SEC-029 (e) Praez. 3; berichtigt
   *    statt geloescht, REQ-OPS-036 Praez. 6) ───────────────────────────────
   * BIS DAHIN lauteten die Ausgaenge (2) und (3) „kein Admin, aber eine
   * Kennung → `subscribe` mit dieser Kennung" und „kein Admin und keine
   * Kennung → kein Abo", und die Kennung war der GEWAEHLTE Mandant
   * (Parameter `rueckfallCompanyId`). **Seit dem Entscheid zu OP-256 zaehlt
   * fuer einen Nicht-Admin der GANZE ANSPRUCH (`authStore.companyIds`), nicht
   * die Wahl;** der Parameter ist entfallen, weil er keine Wirkung mehr haette
   * (ein Parameter ohne Wirkung ist eine Behauptung, er habe eine). Die
   * Ausgaenge lauten ab jetzt — und sie sind vollstaendig:
   *   (1) Admin → unveraendert filterlos, sortiert nach `name` in der Abfrage.
   *   (2) Nicht-Admin, ZWEI ODER MEHR Kennungen → EINE Abfrage
   *       mit einem `in`-Filter auf `companyId` mit dem Anspruch, OHNE Sortierung
   *       in der Abfrage (die Abfrageform steht in Worten: der Index-Waechter
   *       liest Quelltext und sieht Kommentare nicht als solche; sortiert wird
   *       an der gemeinsamen Uebernahmestelle, damit kein zusammengesetzter
   *       Index entsteht). `subscribedCompanyId` bleibt `null`,
   *       `subscribedCompanyIds` traegt die Liste.
   *   (3) Nicht-Admin, GENAU EINE Kennung → der mandantengefilterte Weg mit
   *       DIESER Kennung (`==`).
   *   (4) Nicht-Admin, LEERER Anspruch → **kein Abo**; ein laufendes wird
   *       abgeraeumt und die Liste geleert (REQ-SEC-028 (a): leer heisst
   *       „kein Mandant", nie „alle").
   * **Der Vorbehalt steht weiterhin VOR jeder Abfrage** (Praez. 1 (3)) — die
   * Rules filtern nicht, sie weisen ab: jede Kennung der `in`-Liste liegt im
   * Anspruch, sonst wiese Firestore die ganze Abfrage ab (Praez. 3 (2)).
   *
   * **DIE LISTE FOLGT DEM ANSPRUCH OHNE NEULADEN (Praez. 3 (5)).** Die Funktion
   * setzt eine Beobachtung auf `authStore.isAdmin` und `authStore.companyIds`
   * auf; aendert sich der Anspruch (Claim-Aktualisierung), laeuft dieselbe
   * Weiche erneut, und `starteAbo` raeumt das vorige Abo VOR dem neuen ab. Es
   * gibt dafuer keinen Schreibweg und kein erzwungenes Erneuern des ID-Tokens:
   * folgt der Anspruch im Client erst mit der naechsten Token-Erneuerung, folgt
   * die Liste ebenso spaet (die hingenommene Grenze). Die Beobachtung endet mit
   * {@link unsubscribe} und mit jedem {@link subscribe} von aussen.
   *
   * ⚠ BENANNTE GRENZE (REQ-OPS-036): mehr als {@link IN_FILTER_GRENZE}
   * Kennungen nimmt Firestore in einem `in`-Filter nicht an. Ausgeschlossen
   * ist das allein durch `MAX_MANDANTEN_JE_BENUTZER` — gehalten von der
   * Zusicherung in `test/firestoreIndexes.test.ts`, Abschnitt 2c, nicht von
   * einem Zweig hier.
   */
  function subscribeAlleMandanten() {
    folgeDemAnspruch();
    // ⚠ DER VORBEHALT STEHT VOR DER ABFRAGE (Praez. 1 (3)) — er ist die erste
    // Weiche dieser Funktion und nicht ein Filter hinter dem Ergebnis.
    if (!useAuthStore().isAdmin) {
      const kennungen = kennungenDesAnspruchs();
      if (kennungen.length === 0) {       // (4) kein Abo
        raeumeReichweiteAb();
        return;
      }
      if (kennungen.length === 1) {       // (3) der mandantengefilterte Weg
        abonniereMandant(kennungen[0]!);
        return;
      }
      starteAbo({                         // (2) die Liste des Anspruchs
        companyId:  null,
        companyIds: kennungen,
        // Ohne Sortierung IN DER ABFRAGE — sortiert wird in `starteAbo`.
        ziel: () => query(collection(db, 'printservers'), where('companyId', 'in', kennungen)),
      });
      return;
    }
    starteAbo({
      companyId: null,
      ziel: () => query(collection(db, 'printservers'), orderBy('name')),
    });
  }

  /**
   * Die Kennungen des Anspruchs — getrimmt, ohne leere und ohne doppelte, in
   * der Reihenfolge des Anspruchs. Eine doppelt gefuehrte Kennung ist EINE:
   * sie fuehrt auf den `==`-Weg, nicht auf eine `in`-Abfrage mit einem Wert.
   */
  function kennungenDesAnspruchs(): string[] {
    const roh = (useAuthStore().companyIds ?? []) as readonly unknown[];
    const kennungen: string[] = [];
    for (const k of roh) {
      const s = typeof k === 'string' ? k.trim() : '';
      if (s && !kennungen.includes(s)) kennungen.push(s);
    }
    return kennungen;
  }

  /**
   * Ausgang (4): der Anspruch ist leer. Ein laufendes Abo wird abgeraeumt, die
   * Liste geleert und das Gemerkte fuer den Rueckweg verworfen — die
   * Reichweite ist leer, und „Erneut verbinden" darf keine alte zurueckholen.
   */
  function raeumeReichweiteAb() {
    _unsub?.();
    _unsub = null;
    letztesAbo = null;
    servers.value              = [];
    isLoading.value            = false;
    error.value                = null;
    isSubscribed.value         = false;
    subscribedCompanyId.value  = null;
    subscribedCompanyIds.value = null;
  }

  /**
   * DIE BEOBACHTUNG DES ANSPRUCHS (T-M45.9, Praez. 3 (5)). Laeuft in einem
   * EIGENEN, abgeloesten Wirkungsbereich — nicht in dem der Ansicht, die
   * gerade `onMounted` ausfuehrt: ihr Ende steuert allein dieser Store
   * ({@link beendeAnspruchsFolge}). Wird nur einmal aufgesetzt; ein erneuter
   * Durchlauf der Weiche aus der Beobachtung heraus setzt keine zweite auf.
   *
   * Beobachtet wird ein SCHLUESSEL und nicht das Feld selbst: `companyIds` ist
   * ein berechneter Wert, der bei jeder Token-Erneuerung ein neues Feld liefert.
   * Nur eine inhaltliche Aenderung des Anspruchs setzt das Abo neu auf.
   */
  let _anspruchsFolge: EffectScope | null = null;
  function folgeDemAnspruch() {
    if (_anspruchsFolge) return;
    const bereich = effectScope(true);
    bereich.run(() => {
      watch(
        () => (useAuthStore().isAdmin ? '*admin*' : [...kennungenDesAnspruchs()].sort().join('\n')),
        () => subscribeAlleMandanten(),
      );
    });
    _anspruchsFolge = bereich;
  }

  function beendeAnspruchsFolge() {
    _anspruchsFolge?.stop();
    _anspruchsFolge = null;
  }

  /**
   * DER RUECKWEG VON HAND — „Erneut verbinden" (T-M35.2 Nacharbeit N1,
   * REQ-OPS-054 (a), REQ-OPS-053 (d)).
   *
   * Nimmt **das Abo wieder auf, das lief** — mit demselben Ziel und derselben
   * Reichweite. Gab es noch keines, geschieht nichts: es gibt dann nichts
   * wiederaufzunehmen, und ein erfundenes Abo waere eine Reichweite, die
   * niemand gewaehlt hat.
   *
   * ⚠ **WARUM DIE AUFRUFSTELLE KEINEN MANDANTEN MEHR UEBERGIBT:** der
   * Verbindungshinweis kennt nur den GEWAEHLTEN Mandanten, nicht die Reichweite
   * des toten Abos. Uebergaebe er ihn, bekaeme ein Admin unter
   * `/settings/printserver` nach dem Klick die Liste eines einzigen Mandanten
   * zurueck (REQ-SEC-029 (e) Praez. 1 (1) verletzt), und ohne gewaehlten
   * Mandanten bekaeme er gar nichts — der Hinweis bliebe stehen, obwohl er zum
   * Handeln auffordert (REQ-OPS-054 (a) verletzt). **Die Reichweite gehoert dem
   * Store, nicht der Aufrufstelle** — dieselbe Begruendung wie bei der
   * Rollenweiche (REQ-SEC-029 (e) Praez. 1 (4)).
   *
   * *Der Weg fuehrt ueber dieselbe Uebernahmestelle wie jedes andere Abo
   * (REQ-OPS-038): eine laufende Rueckzugsstaffel wird dabei abgebrochen, weil
   * `starteAbo` die vorige Anmeldung abraeumt (REQ-OPS-053 (d)).*
   */
  function erneutAbonnieren() {
    if (!letztesAbo) return;
    starteAbo(letztesAbo);
  }

  function unsubscribe() {
    beendeAnspruchsFolge();
    _unsub?.();
    _unsub = null;
    // Die Liste wird NICHT geleert (Bauform des Druckerstores): eine Ansicht,
    // die den Bestand noch anzeigt, soll ihn nicht mitten im Verlassen
    // verlieren. Sichtbar wird ab jetzt aber, dass niemand mehr zuhoert.
    isSubscribed.value        = false;
    subscribedCompanyId.value = null;
    subscribedCompanyIds.value = null;
  }

  /**
   * Liest EINEN Druckserver nach — ohne Abo und ohne die Liste zu beruehren
   * (T-M30.7 Nacharbeit N1a, REQ-SEC-029 (a)).
   *
   * WOFUER: die zwei Detailansichten loesten ihren Datensatz bisher aus der
   * FILTERLOSEN Liste auf. Mit dem Mandantenfilter in der Abfrage waere ein
   * Druckserver AUSSERHALB des gewaehlten Mandanten fuer einen Admin nicht mehr
   * per Adresse zu oeffnen — das ist ein Verlust und keine Trennung. Die Rules
   * erlauben `printservers/{id}` einem Admin UND einem Mitglied desselben
   * Mandanten (`get`); der Einzelabruf gibt dem Admin seine Reichweite zurueck,
   * ohne einem `user` etwas zu geben.
   *
   * GENAU EIN `getDoc`, und „Dokument fehlt" ist ein eigener ZWEIG, kein
   * Fehlerpfad — `null` heisst „es gibt kein solches Dokument". Bauform wie
   * `companyStore.fetchCompanyById`; der Rechtefehler-Wiederholversuch aus
   * REQ-SEC-028 (f) laeuft mit.
   *
   * ⚠ GRENZE, benannt: der so gelesene Datensatz ist eine MOMENTAUFNAHME und
   * kein Abo — `status` und `lastPing` eines fremden Mandanten stehen still.
   * Das ist tragbar: der Fall ist die Adresszeile eines Admins ausserhalb
   * seines gewaehlten Mandanten, nicht die Ueberwachung.
   */
  async function fetchServerById(id: string): Promise<PrintServer | null> {
    const key = (id ?? '').trim();
    if (!key || key === 'new') return null;
    const snap = await mitAnspruchsWiederholung(
      'printservers',
      () => getDoc(doc(db, 'printservers', key)),
    );
    return snap.exists() ? ({ id: snap.id, ...snap.data() } as PrintServer) : null;
  }

  async function addServer(data: Omit<PrintServer, 'id'>) {
    await addDoc(collection(db, 'printservers'), {
      ...data,
      status:    'offline',
      createdAt: serverTimestamp(),
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DIE KASKADE BEIM UMHAENGEN UND LOESCHEN (T-M45.6, REQ-DM-074)
  //
  // Befund 2409_2: bis zum 2026-09-28 waren `updateServer` ein nacktes
  // `updateDoc` und `deleteServer` ein nacktes `deleteDoc`; keines fasste
  // `printers` an. Ein umgehaengter Druckserver liess die Drucker seiner alten
  // Firma mit seiner `printserverId` zurueck, und der jetzt fremde Pi druckte
  // weiter (berichtigt statt geloescht, REQ-OPS-036 Praez. 6).
  //
  // DER WEG HAT ZWEI SCHRITTE, und beide liegen hier:
  //   (1) `erhebeZugewieseneDrucker` — VOR der Handlung, damit der Betreiber
  //       sieht, was geschieht (REQ-DM-074 (1)/(2)).
  //   (3) `updateServer`/`deleteServer` mit der Erhebung als PFLICHTPARAMETER
  //       — EIN `writeBatch`: der Druckserver UND an jedem betroffenen Drucker
  //       `printserverId: deleteField()`. Beides oder nichts.
  // **Es gibt keinen Loeschweg ohne Erhebung** (REQ-DM-074 (4)): der Parameter
  // ist Pflicht, ein Aufrufer ohne ihn bricht in der Typpruefung.
  //
  // *Die Drucker werden NICHT mit umgehaengt — sie wechselten die Firma, eine
  // Mandantengrenzverletzung. Die Kaskade schreibt nur Drucker der bisherigen
  // Firma, deren `companyId` sich nicht aendert; ein `user` mit beiden Firmen
  // im Anspruch scheitert damit nicht an den Rules (REQ-SEC-029 (c)).*
  //
  // ⚠ BENANNTE GRENZEN (REQ-OPS-036):
  //   • Ein Drucker, der zwischen Erhebung und Bestaetigung neu zugewiesen
  //     wird, ist nicht in der Kaskade. Das Fenster ist die Lesezeit eines
  //     Dialogs; es gibt keine Transaktion ueber eine Abfrage im Web SDK.
  //   • Ein `writeBatch` traegt hoechstens 500 Schreibvorgaenge. Ueberschreitet
  //     eine Kaskade das, weist Firestore den GANZEN Batch ab — es wird nichts
  //     halb geschrieben; der Fehler erscheint in der Ansicht.
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Die Drucker, die auf diesen Druckserver zeigen (REQ-DM-074 (1)):
   * `printers` mit `companyId == <bisherige Firma>` UND
   * `printserverId == <id>` — zwei Gleichheitsbedingungen, kein
   * zusammengesetzter Index (registriert in `test/firestoreIndexes.test.ts`).
   * **Archivierte Drucker zaehlen mit** — gefiltert wird nicht.
   *
   * Ohne bisherige Firma ist die Erhebung leer: die Form in REQ-DM-074 (1)
   * setzt eine bisherige Firma voraus, der Bestand ohne Firma ist 0 (T-M45.3
   * Punkt 4: 0 von 11) und REQ-DM-074 (5) verhindert neue — siehe
   * `utils/printserverKaskade.ts`, benannte Grenze. (Nicht die Rules: einem
   * Admin, dem einzigen, der einen solchen Druckserver lesen kann, erlaubten
   * sie die Abfrage auch ohne Firmenfilter.)
   */
  async function erhebeZugewieseneDrucker(
    printserverId: string,
    bisherigeCompanyId: string,
  ): Promise<ZugewiesenerDrucker[]> {
    const id  = (printserverId ?? '').trim();
    const cid = (bisherigeCompanyId ?? '').trim();
    if (!id || !cid) return [];
    const snap = await mitAnspruchsWiederholung('printers', () => getDocs(query(
      collection(db, 'printers'),
      where('companyId', '==', cid),
      where('printserverId', '==', id),
    )));
    // Unsortiert: die Reihenfolge im Dialog bildet `utils/printserverKaskade.ts`
    // (EINE Sortierregel; `printserverReichweite.test.ts` haelt die Zahl der
    // `localeCompare`-Stellen in diesem Store bei eins).
    return snap.docs
      .map(d => ({ id: d.id, name: String((d.data() as { name?: unknown }).name ?? '') }));
  }

  /** Nimmt jedem betroffenen Drucker die Zuweisung — in DENSELBEN Batch. */
  function entferneZuweisungen(
    batch: ReturnType<typeof writeBatch>,
    betroffene: readonly ZugewiesenerDrucker[],
  ): void {
    // `deleteField()` und nie `''` oder `null` (REQ-DM-072 (c)).
    for (const p of betroffene) {
      batch.update(doc(db, 'printers', p.id), { printserverId: deleteField() });
    }
  }

  async function updateServer(
    id: string,
    data: Partial<Omit<PrintServer, 'id'>>,
    betroffene: readonly ZugewiesenerDrucker[],
  ) {
    const batch = writeBatch(db);
    batch.update(doc(db, 'printservers', id), data);
    entferneZuweisungen(batch, betroffene);
    await batch.commit();
  }

  async function deleteServer(id: string, betroffene: readonly ZugewiesenerDrucker[]) {
    const batch = writeBatch(db);
    batch.delete(doc(db, 'printservers', id));
    entferneZuweisungen(batch, betroffene);
    await batch.commit();
  }

  return {
    servers, isLoading, hasLoaded, error, isSubscribed, subscribedCompanyId,
    subscribedCompanyIds,
    subscribe, subscribeAlleMandanten, erneutAbonnieren, unsubscribe,
    fetchServerById, addServer, updateServer, deleteServer,
    erhebeZugewieseneDrucker,
  };
});
